// Library methods
import { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// Components
import TodayExamCard from "./TodayExamCard";
import LinkDeviceModal from "../../../components/UI/LinkDeviceModal";
import UnavailableDeviceModal from "../../../components/UI/UnavailableDeviceModal";
import LiveUpdateModal from "../../../components/UI/LiveUpdateModal";
import ListHeader from "../../../components/UI/ListHeader";
import PaginationWrapper from "../../../components/UI/PaginationWrapper";
import SnackbarMessage from "../../../components/UI/SnackbarMessage";
import Loader from "../../../components/UI/Loader";
import EditExamSlider from "../../Patients/ExamsList/EditExamSlider";
import AddExamSlider from "../../Patients/ExamsList/AddExamSlider";
import SelectPatientModal from "../../../components/UI/SelectPatientModal";
import { useTheme } from "@emotion/react";
import { IconButton, useMediaQuery } from "@mui/material";
import { PlusIcon } from "../../../assets/icons";

// Utilities
import { getComparator } from "../../../utils/tableHelper";
import { itemsRendering, pagesCount } from "../../../utils/paginationHelper";
import {
  getAllExamSections,
  getVisualField,
} from "../../../utils/examReportsHelper";
import useClinic from "../../../hooks/useClinic";
import { useDevices } from "../../../hooks/useDevices";
import { useExamStatus } from "../../../hooks/useExamStatus";

// Contexts
import { ValidatePDFContext } from "../../../contexts/ValidatePDFContext";
import { useExamListContext } from "../../../contexts/ExamListContext";

// Styles
import { CustomIconButtonDark } from "../../../styles/muiStylesHelper";

const TodayExamsList = ({
  connection,
  isLoading,
  patients,
  refetchDevices,
  isLoadingSignalR,
}) => {
  // clinic context
  const { clinicId } = useClinic();

  // internationalization
  const { t } = useTranslation();

  const {
    rows,
    sectionTypeOrder,
    devicesWithExams,
    availableDevicesOptions,
    linkLoading,
    linkDeviceModal,
    unavailableDeviceModal,
    editExamId,
    editExamDevice,
    isSavingExam,
    addModal,
    editModal,
    examCards,
    scheduledDate,
    toast,
    actions,
  } = useExamListContext();
  const {
    refetchExams,
    handleLinkDevice,
    handleLinkDeviceClick,
    handleLinkDeviceClose,
    handleUnavailableDeviceClose,
    handleAddClose,
    handleAddOpen,
    handleEditClose,
    handleEditOpen,
    setToast,
    setEditExamDevice,
    addExam,
    editExam,
    onEditExamCallback,
    onAddExamCallback,
  } = actions;

  const { unlinkDeviceModal, handleUnlinkDeviceClick } = useDevices(
    connection,
    clinicId,
    patients,
    devicesWithExams
  );

  const { getExamStatus } = useExamStatus();

  const orderByPatientName = ["firstName", "lastName"];
  const orderByTypeAlg = ["type", "algorithm", "gridType"];

  // states init
  const [order, setOrder] = useState("asc");
  const [orderByOptions, setOrderByOptions] = useState(orderByPatientName);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState();
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const maxNumOfRows = 4;
  const examCardRef = useRef(null);

  const theme = useTheme();
  const smallerThanXlScreen = useMediaQuery(theme.breakpoints.down("xl"));

  // head rows
  const headCells = [
    // This one is displayed before the others, on a different grid.
    {
      id: "adjustment",
      label: null,
      gridSize: 0.3,
      orderUsing: [],
    },
    {
      id: "device",
      label: t("exams_column_device"),
      gridSize: 1.8,
      orderUsing: [],
    },
    // The below ones are displayed within the same grid, and should sum to 11 (that will be displayed together with a checkbox of grid size 1).
    {
      id: "patient",
      label: t("current_patient"),
      gridSize: 2,
      orderUsing: [...orderByPatientName, ...orderByTypeAlg],
    },
    {
      id: "status",
      label: t("exams_column_status"),
      gridSize: 2,
      orderUsing: ["statusOrder", ...orderByPatientName, ...orderByTypeAlg],
    },
    {
      id: "eye",
      label: t("exams_column_eye"),
      gridSize: 2.4,
      orderUsing: ["eyesOrder", ...orderByPatientName, ...orderByTypeAlg],
    },
    {
      id: "type",
      label: t("exams_column_type"),
      gridSize: 2.8,
      orderUsing: [...orderByTypeAlg, ...orderByPatientName],
    },
    {
      id: "manage",
      label: "",
      gridSize: 1,
    },
  ];

  // pagination change
  const onPaginationChange = (event, value) => {
    setPage(value);
  };

  // handle sort Request (it can now order considering two fields)
  const handleRequestSort = (event, property) => {
    const isAsc =
      orderByOptions.length &&
      orderByOptions[0] === property[0] &&
      order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderByOptions(property);
  };

  // Filtered rows
  const filteredRows = useMemo(
    () =>
      rows ??
      []
        .filter((row) => getAllExamSections(row)?.length) // filter exams with no sections
        .map((row) => {
          const rowSections = getAllExamSections(row);
          const rowGrid = rowSections?.[0]?.gridType ?? "";
          const examStatus = getExamStatus(row, devicesWithExams);
          return {
            ...row,
            // adding some helpful information to the exam
            statusOrder: examStatus?.status,
            tutorialOrder: row.requiresTutorial ? t("word_yes") : t("word_no"),
            visualFieldOrder: getVisualField(rowGrid).toLowerCase(),
            eyesOrder: getAllExamSections(row)
              .sort((a, b) => {
                if (typeof a.eye === "string" && typeof b.eye === "string") {
                  return a.eye.localeCompare(b.eye);
                }
                // or any other logic to handle non-string cases
                return 0;
              })
              .map((section) => section.eye)
              .join(""),
            deviceName: row.associatedDevice?.device?.alias || "-",
          };
        }),
    [devicesWithExams, getExamStatus, rows, t]
  );

  // sorted rows
  const sortedRows = useMemo(() => {
    if (!filteredRows?.length) return [];
    const notStartedDeviceExams = [];
    const notLiveExams = [];
    const liveExams = filteredRows.filter((row) => {
      const device = devicesWithExams?.find(
        (item) => item?.associatedExam?.exam?.id === row?.id
      );

      if (
        device &&
        device?.deviceStatus?.stage?.stageType &&
        device?.deviceStatus?.stage?.stageType !== "Not started"
      )
        return true;
      if (device) {
        notStartedDeviceExams.push(row);
        return false;
      } else {
        notLiveExams.push(row);
        return false;
      }
    });
    const sortedLiveExams = (liveExams || []).sort(
      // getComparator(order, orderBy, orderByAlternative)
      getComparator(order, orderByOptions)
    );
    const sortedNotStartedDeviceExams = (notStartedDeviceExams || []).sort(
      // getComparator(order, orderBy, orderByAlternative)
      getComparator(order, orderByOptions)
    );
    const sortedNotLiveExams = (notLiveExams || []).sort(
      // getComparator(order, orderBy, orderByAlternative)
      getComparator(order, orderByOptions)
    );

    return [
      ...sortedLiveExams,
      ...sortedNotStartedDeviceExams,
      ...sortedNotLiveExams,
    ];
  }, [devicesWithExams, filteredRows, order, orderByOptions]);

  const rowsToRender = useMemo(() => {
    return itemsRendering(
      sortedRows,
      page,
      smallerThanXlScreen ? 6 : maxNumOfRows
    ).filter(
      // only show exams that have at least 1 section
      (row) => getAllExamSections(row)?.length > 0
    );
  }, [page, sortedRows, maxNumOfRows, smallerThanXlScreen]);

  const totalPages = useMemo(
    () => pagesCount(sortedRows, smallerThanXlScreen ? 6 : maxNumOfRows),
    [maxNumOfRows, smallerThanXlScreen, sortedRows]
  );

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          component="div"
          variant="h7"
          sx={{
            fontWeight: "bold",
            fontSize: { sm: "16px", lg: "20px" },
          }}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {t("Today")}
        </Typography>
        <Tooltip title={t("exams_add_exam")}>
          <IconButton
            aria-label="add"
            sx={CustomIconButtonDark}
            onClick={() => {
              setSelectedPatient(null);
              setOpenPatientModal(true);
            }}
          >
            <PlusIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Loader containerHeight="30vh" />
      ) : (
        <Grid container mt={{ xs: 0, sm: 2 }}>
          <Grid item xs={12}>
            <ListHeader
              containerPaddingX={0}
              headCells={headCells}
              order={order}
              orderBy={orderByOptions?.[0] ?? "firstName"}
              onRequestSort={handleRequestSort}
              rowCount={rowsToRender.length}
              showCheckbox={false}
              checkBoxGrid={0}
              shouldDisplayGrid="lg"
              displayFirtsBeforeCheckbox
            />
            {rowsToRender.length > 0 ? (
              <Box my={4}>
                {rowsToRender.map((row) => {
                  return (
                    <TodayExamCard
                      key={row.id}
                      row={row}
                      patientId={row?.patient?.id}
                      handleEditClick={(exam) => {
                        setSelectedRow(row);
                        handleEditOpen(exam);
                      }}
                      handleLinkDeviceClick={handleLinkDeviceClick}
                      handleUnlinkDeviceClick={handleUnlinkDeviceClick}
                      associatedExamDevices={devicesWithExams}
                      linkLoading={linkLoading}
                      unlinkLoading={linkLoading}
                      ref={examCardRef}
                    />
                  );
                })}
              </Box>
            ) : (
              <Box
                my={4}
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="52px"
              >
                <Typography
                  noWrap
                  variant="body1"
                  sx={{ textTransform: "capitalize" }}
                  color="text.secondary"
                >
                  {t("word_no_today_exams")}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {/* Modal to select a patient before adding a new exam */}
      <SelectPatientModal
        open={openPatientModal}
        onClose={() => setOpenPatientModal(false)}
        onConfirm={() => {
          setOpenPatientModal(false);
          handleAddOpen();
        }}
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
      />

      {/* Add exam modal */}
      <AddExamSlider
        isLoading={isSavingExam}
        visible={addModal}
        onClose={handleAddClose}
        onConfirm={() =>
          addExam({
            patientId: selectedPatient?.id,
            selectedExamCards: examCards.selected,
            scheduledDate,
            sectionTypeOrder,
            onAddExamCallback,
          })
        }
        patient={selectedPatient}
      />

      {/* Edit exam modal */}
      <EditExamSlider
        isLoading={isSavingExam}
        visible={editModal}
        onClose={handleEditClose}
        onConfirm={() =>
          editExam({
            selectedExamCards: examCards.selected,
            sectionTypeOrder,
            scheduledDate,
            patientId: selectedRow?.patient?.id,
            editExamId,
            onEditExamCallback,
          })
        }
        patient={selectedRow?.patient}
      />

      {rows?.length > rowsToRender?.length && (
        <PaginationWrapper
          page={page}
          count={totalPages}
          onChange={onPaginationChange}
        />
      )}

      {/* Unlink device exam modal */}
      {unlinkDeviceModal}

      {/* Link device modal */}
      <LinkDeviceModal
        open={linkDeviceModal}
        onClose={handleLinkDeviceClose}
        onConfirm={handleLinkDevice}
        onCancel={handleLinkDeviceClose}
        devicesOptions={availableDevicesOptions}
        editExamDevice={editExamDevice}
        setEditExamDevice={setEditExamDevice}
        loading={linkLoading}
      />

      {/* Unavailable device modal */}
      <UnavailableDeviceModal
        open={unavailableDeviceModal}
        onClose={handleUnavailableDeviceClose}
        onCancel={handleUnavailableDeviceClose}
      />

      {/* Live update modal */}
      <ValidatePDFContext.Provider value={{ forBackendPdf: false }}>
        <LiveUpdateModal
          connection={connection}
          refetchDevices={refetchDevices}
          refetchExams={refetchExams}
          rows={rows}
          isLoadingSignalR={isLoadingSignalR}
        />
      </ValidatePDFContext.Provider>

      {/* Add exam success/failure toasts */}
      <SnackbarMessage
        open={Boolean(toast.message)}
        onClose={() => setToast({ success: false, message: null })}
        success={toast.success}
        autoHideDuration={8000}
        text={toast.message}
      />
    </>
  );
};

export default TodayExamsList;
